<template>
  <div
    class="participant"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
  >
    <template v-if="participant">
      <v-avatar
        v-if="!hover"
        class="user-avatar blue white--text"
        size="60"
      >
        {{ initials(participant.identity) }}
      </v-avatar>
      <audio
        :ref="`audio-${participant.sid}`"
        class="user-audio"
        autoplay
      ></audio>
      <video
        :ref="`video-${participant.sid}`"
        class="user-video"
        autoplay
        muted
        playsinline
      ></video>
    </template>
    <v-fade-transition>
      <v-overlay
        v-if="hover"
        opacity="0"
        absolute
        color="primary"
      >
        <div class="user-controls">
          <v-btn
            :disabled="screenshotLoading"
            fab
            :loading="screenshotLoading"
            text
            :small="small"
            @click="captureImage"
          >
            <v-icon>mdi-camera-plus</v-icon>
          </v-btn>
          <v-btn
            class="ml-1"
            :color="isRecording ? 'error' : ''"
            fab
            :small="small"
            text
            @click="openRecordDialog"
          >
            <v-icon>mdi-video-box{{ isRecording ? "-off" : "" }}</v-icon>
          </v-btn>
          <v-btn
            class="ml-1"
            fab
            :small="small"
            text
            @click="pin"
          >
            <v-icon>mdi-pin{{ !!participant.isPined ? "-off" : "" }}</v-icon>
          </v-btn>
          <v-btn
            v-if="!small"
            class="ml-1"
            fab
            text
            @click="fullscreen"
          >
            <v-icon>mdi-fullscreen</v-icon>
          </v-btn>
        </div>
        <div
          v-if="isRecording"
          class="mt-2"
        >
          {{ $t("messages.information.screen_recording") }}
        </div>
      </v-overlay>
    </v-fade-transition>
    <ConfirmDialog
      v-model="confirmDialogIsOpen"
      @cancel="closeRecordDialog"
      @confirm="toggleRecord"
      :display="display"
    />
  </div>
</template>

<script>
import { MeetingService } from "@/services"
import InspectionService from "@/services/InspectionService"
import { initials } from "@/utils/string"
import { toTimestamp } from "@/utils/datetime"
import ConfirmDialog from "@/components/generics/dialogs/ConfirmDialog.vue"

export default {
  name: "Participant",
  components: { ConfirmDialog },
  props: {
    inspection: {
      type: Object,
      required: true
    },
    participant: {
      type: Object,
      default: null
    },
    small: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hover: false,
      screenshotLoading: false,
      isRecording: false,
      confirmDialogIsOpen: false,
      display: {
        title: "",
        text: "",
        prevention: ""
      }
    }
  },
  methods: {
    fullscreen() {
      let element = this.$refs[`video-${this.participant.sid}`]
      element.requestFullscreen()
    },
    pin() {
      this.$emit("pined", this.participant)
    },
    initials,
    notify(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    toggleRecord() {
      const action = this.isRecording ? "stop" : "start"
      MeetingService.recording(this, this.inspection.id, action, this.participant.identity)
        .then(r => {
          this.isRecording = !this.isRecording
          const message =
            action === "start"
              ? this.$t("commons.recorded_start")
              : this.$t("commons.recorded_stop")
          this.notify(message, "info")
        })
        .catch(error => {
          this.notify(error, "error")
        })
        .finally(() => {
          this.closeRecordDialog()
        })
    },
    closeRecordDialog() {
      this.confirmDialogIsOpen = false
    },
    openRecordDialog(item) {
      if (this.isRecording) {
        this.toggleRecord()
      } else {
        this.confirmDialogIsOpen = true
        this.display = {
          title: this.$t("commons.video_record"),
          text: this.$t("messages.information.record_video"),
          prevention: ""
        }
      }
    },
    captureImage() {
      let t = this

      this.screenshotLoading = true

      const video = this.$refs[`video-${this.participant.sid}`]
      const canvas = document.createElement("canvas")
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext("2d").drawImage(video, 0, 0)

      let img = canvas.toDataURL("image/png")
      let captureTime = toTimestamp().toString(16)
      let captureData = {
        name: "capture_" + this.inspection.name + "_" + captureTime,
        file: img
      }

      InspectionService.unassignedPictures
        .create(this.inspection.id, captureData)
        .then(r => {
          t.$store.commit("app/clearMessages")
          t.$store.commit("app/message", {
            msg: this.$t("messages.success.saved", { entity: this.$tc("commons.picture", 1) }),
            color: "success"
          })
        })
        .catch(err => {
          console.error(err)
          t.$store.commit("app/clearMessages")
          t.$store.commit("app/message", {
            msg: this.$t("messages.error.smtg_wrong"),
            color: "error"
          })
        })
        .finally(() => {
          t.screenshotLoading = false
        })
    }
  },
  computed: {
    videoDimensions() {
      const sid = this.participant.sid
      let dim = { width: null, height: null }
      if (!sid || !this.$refs[`video-${sid}`]) return dim

      dim.width = this.$refs[`video-${sid}`].clientWidth
      dim.height = this.$refs[`video-${sid}`].clientHeight

      return dim
    }
  }
}
</script>

<style lang="scss" scoped>
.user-avatar {
  opacity: 0.6;
  // Item position
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}
.user-audio {
  display: none;
}
.user-controls {
  background-color: rgb(49, 49, 49);
  height: 100%;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  opacity: 0.6;
  border-radius: 50px;
}
.user-video {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}
</style>
